/**
 * Advanced → Agent Playbooks — the builder's designer for obj_type "Y".
 *
 * Give it a GOAL and the details; the Ask-AI agent BUILDS it — the
 * ``playbook-designer`` sub-agent (its skill, live grounding on the app's real
 * objects, actions and columns, and its questions asked in the Ask-AI panel)
 * writes the playbook and ends on ``playbook_verify.py --rederive``, which
 * derives the access PROFILE the persona needs and proposes what else the goal
 * requires: approval chains, integration events/APIs, a scheduler, and the
 * IOFlow connection and flow for each external system. This screen reloads
 * when the agent writes the file. The proposals are a checklist; Apply runs the
 * accepted ones in dependency order and verifies the result. IOFlow flows are
 * handed to Ask-AI's IOFlow designer, which reads IOFlow's own format.
 *
 * Colours are theme variables only (light and dark).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useProjectStore } from '@/store/projectStore'
import toast from 'react-hot-toast'
import {
  Chip, cardStyle, inputStyle, labelStyle, schedulerColors as C,
} from '@twasta/page-runtime/shell/jobs'
import {
  playbooksApi, type DeriveResult, type OpenQuestion, type Playbook, type PlaybookStep,
  type ProposalItem, type VerifyResult,
} from '@/api/playbooks'

const KIND_ICON: Record<string, string> = {
  query: 'search', reason: 'psychology', write: 'edit_note', process: 'settings',
  report: 'description', human_input: 'person', external_input: 'mail', approval: 'verified',
  integration_call: 'hub', wait: 'hourglass_empty', for_each: 'repeat', tool: 'build',
}

const STATUS_TONE: Record<string, 'ok' | 'err' | 'warn' | 'info' | 'muted' | 'accent'> = {
  proposed: 'info', accepted: 'accent', applied: 'ok', verified: 'ok', handoff: 'warn',
  pending_admin: 'warn', failed: 'err', rejected: 'muted',
}

const STATUS_LABEL: Record<string, string> = {
  proposed: 'Proposed', accepted: 'Accepted', applied: 'Applied', verified: 'Verified',
  handoff: 'Build in Ask-AI', pending_admin: 'Awaiting admin', failed: 'Failed', rejected: 'Skipped',
}

const btn = (primary = false, danger = false): React.CSSProperties => ({
  height: 32, padding: '0 14px', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600,
  cursor: 'pointer', border: `1px solid ${danger ? C.err : primary ? C.accent : C.line}`,
  background: primary ? C.accent : 'transparent', color: primary ? '#fff' : danger ? C.err : C.ink,
})

function stepSummary(s: PlaybookStep): string {
  switch (s.kind) {
    case 'query': return `Read ${s.obj_name}${s.filters?.length ? ` where ${s.filters.map((f) => `${f.column} ${f.op || 'eq'} ${String(f.value)}`).join(' and ')}` : ''}`
    case 'write': return s.op === 'action' ? `Run "${s.action_name}" on ${s.obj_name}` : `${s.op} ${s.obj_name}`
    case 'reason': return String(s.prompt || '')
    case 'process': return `Run process ${s.obj_name}`
    case 'report': return `Render report ${s.obj_name}`
    case 'human_input': return `Ask ${s.assignee_role || s.assignee_user || 'a person'} (Work List)`
    case 'external_input': return `Email a one-time link to ${s.recipient} (${s.mode === 'approve_reject' ? 'approve / reject' : 'input'})`
    case 'approval': return `Approval chain for "${s.action_name}" on ${s.obj_name}`
    case 'integration_call': return s.event ? `Send event ${s.event} to IOFlow` : `Call integration API ${s.api}`
    case 'wait': return s.event ? `Wait for event ${s.event}${s.timeout ? ` (up to ${s.timeout})` : ''}` : `Wait ${s.duration || ''}`
    case 'for_each': return `For each item in ${s.over}`
    case 'tool': return `Run the playbook's tool "${String(s.tool || '')}"`
    default: return s.kind
  }
}

function StepTree({ steps, depth = 0 }: { steps: PlaybookStep[]; depth?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {steps.map((s, i) => (
        <div key={`${s.id}-${i}`} style={{ marginLeft: depth * 22 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px',
            borderRadius: 8, background: depth ? 'transparent' : C.panel, border: `1px solid ${C.line}` }}>
            <span className="material-icons" aria-hidden style={{ fontSize: 19, color: C.accent, marginTop: 1 }}>
              {KIND_ICON[s.kind] || 'radio_button_unchecked'}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '0.9125rem', fontWeight: 600, color: C.ink }}>
                {s.title || s.id} <span style={{ color: C.faint, fontWeight: 400, fontSize: '0.8125rem' }}>· {s.kind.replace('_', ' ')}</span>
              </div>
              <div style={{ fontSize: '0.8425rem', color: C.ink2, wordBreak: 'break-word' }}>{stepSummary(s)}</div>
            </div>
          </div>
          {s.kind === 'for_each' && s.body && (
            <div style={{ marginTop: 4, borderLeft: `2px solid ${C.line}`, paddingLeft: 6 }}>
              <StepTree steps={s.body} depth={1} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function Questions({ questions, problems }: { questions: OpenQuestion[]; problems: string[] }) {
  if (!questions.length && !problems.length) return null
  return (
    <div style={{ ...cardStyle, padding: 12, borderColor: C.warn, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ ...labelStyle, color: C.warn }}>Needs your decision</div>
      {problems.map((p, i) => <div key={`p${i}`} style={{ fontSize: '0.875rem', color: C.err }}>{p}</div>)}
      {questions.map((q, i) => (
        <div key={`q${i}`} style={{ fontSize: '0.875rem', color: C.ink }}>
          {q.question}
          {q.options?.length ? (
            <div style={{ color: C.ink2, marginTop: 2 }}>
              Options: {q.options.map((o) => (o === q.recommended ? `${o} (recommended)` : o)).join(' · ')}
            </div>
          ) : null}
        </div>
      ))}
      <div style={{ fontSize: '0.8125rem', color: C.faint }}>
        Tell Ask-AI how to settle these (revise below), or edit the steps and re-derive.
      </div>
    </div>
  )
}

function ProposalList({ project, items, selected, onToggle, onPersona }: {
  project: string
  items: ProposalItem[]
  selected: Set<string>
  onToggle(id: string): void
  onPersona(id: string, persona: string): void
}) {
  const send = (prompt: string) => {
    window.dispatchEvent(new CustomEvent('twasta:agent-send-prompt', { detail: { project, prompt } }))
    toast.success('Sent to Ask-AI')
  }
  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      {items.map((it, i) => {
        const done = ['applied', 'verified', 'pending_admin'].includes(it.status)
        const brief = it.apply_result?.ask_ai_prompt || it.payload?.brief
        return (
          <div key={it.id} style={{ padding: '10px 12px', borderTop: i ? `1px solid ${C.line}` : 'none',
            display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={selected.has(it.id)} disabled={done}
              onChange={() => onToggle(it.id)}
              style={{ marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9125rem', color: C.ink }}>{it.title}</span>
                <Chip tone={STATUS_TONE[it.status] || 'muted'}>{STATUS_LABEL[it.status] || it.status}</Chip>
              </div>
              {it.why && <div style={{ fontSize: '0.8425rem', color: C.ink2 }}>{it.why}</div>}
              {it.kind === 'grant' && (
                <div style={{ fontSize: '0.8125rem', color: C.faint }}>{(it.payload.actions || []).join(', ')}</div>
              )}
              {it.kind === 'schedule' && !done && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <span style={{ fontSize: '0.8125rem', color: C.ink2 }}>Runs as persona</span>
                  <input value={String(it.payload?.target?.persona || '')}
                    placeholder="name of the persona (created at runtime)"
                    onChange={(e) => onPersona(it.id, e.target.value)}
                    style={{ ...inputStyle, height: 28, maxWidth: 260 }} />
                </label>
              )}
              {it.apply_result?.error && (
                <div style={{ fontSize: '0.8425rem', color: C.err, marginTop: 4 }}>{String(it.apply_result.error)}</div>
              )}
              {it.status === 'handoff' && brief && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button type="button" style={btn(true)} onClick={() => send(String(brief))}>Build in Ask-AI</button>
                  <button type="button" style={btn()} onClick={() => {
                    void navigator.clipboard?.writeText(String(brief)); toast.success('Brief copied')
                  }}>Copy brief</button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** The file name the agent is asked to write — the same rule as the server's
 * ``playbook_designer._slug``, so the screen knows which playbook to reload. */
function slugName(text: string): string {
  const s = (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return (s || 'playbook').slice(0, 60)
}

/** The brief handed to the Ask-AI agent, which delegates to its
 * ``playbook-designer`` sub-agent. */
function buildBrief(name: string, goal: string, details: string, exists: boolean): string {
  return [
    `Use the playbook-designer sub-agent to ${exists ? 'REVISE' : 'build'} the agent playbook "${name}" (obj_type Y).`,
    `Write Metadata_Model/playbook_models/model_${name}.json with "name": "${name}".`,
    exists ? 'It already exists: change it in place, and keep the decisions already taken on its proposals.' : '',
    '',
    'GOAL:',
    goal.trim(),
    details.trim() ? `\nDETAILS FROM THE DESIGNER:\n${details.trim()}` : '',
    '',
    'Before writing anything, ask me EVERY question you need answered to build it well — who is asked or approves and how, what happens on a rejection or no answer, which records are in scope, outside systems, how often it runs, and anything this project does not have. Give each question options with a recommended one, and build only from my answers.',
    'Ground every object, action and column in this project\'s real models.',
    `End on playbook_verify.py ${name} --rederive and tell me the steps, the profile the persona needs, where people are asked, and what is proposed.`,
    'Do not apply the proposals — I review and apply them in Advanced → Agent Playbooks.',
  ].filter((l, i, a) => l !== '' || a[i - 1] !== '').join('\n')
}

function Editor({ project, name, onSaved, onBack }: {
  project: string; name: string; onSaved(name: string): void; onBack(): void
}) {
  const [goal, setGoal] = useState('')
  const [details, setDetails] = useState('')
  const [waiting, setWaiting] = useState('')
  const [pbName, setPbName] = useState(name)
  const [pb, setPb] = useState<Playbook | null>(null)
  const [questions, setQuestions] = useState<OpenQuestion[]>([])
  const [problems, setProblems] = useState<string[]>([])
  const [busy, setBusy] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [verify, setVerify] = useState<VerifyResult | null>(null)
  const [jsonOpen, setJsonOpen] = useState(false)
  const [json, setJson] = useState('')

  const adopt = useCallback((p: Playbook, res?: Partial<DeriveResult>) => {
    setPb(p)
    setGoal(p.goal || '')
    setPbName(p.name)
    if (res) {
      setQuestions(res.open_questions || [])
      setProblems(res.problems || [])
    }
    setSelected(new Set((p.proposals?.items || []).filter((i) => i.status === 'proposed').map((i) => i.id)))
    setJson(JSON.stringify(p.steps, null, 2))
  }, [])

  // Load the playbook, and the questions/problems its steps still raise
  // (re-derived, nothing saved), so what the agent left open is visible here.
  const open = useCallback(async (n: string, quiet = false): Promise<boolean> => {
    let p: Playbook
    try {
      p = await playbooksApi.load(project, n)
    } catch (err) {
      if (!quiet) toast.error(err instanceof Error ? err.message : 'Could not open it')
      return false
    }
    adopt(p)
    try {
      const res = await playbooksApi.repropose(project, p)
      setQuestions(res.open_questions || [])
      setProblems(res.problems || [])
    } catch (err) {
      console.warn('[playbooks] could not re-derive on open', err)
    }
    return true
  }, [project, adopt])

  useEffect(() => {
    if (name) void open(name)
  }, [name, open])

  // The agent writes the file from the Ask-AI panel; reload when it reports
  // changed files. A NEW playbook's file may not exist on the first report.
  const agentNonce = useAgentRefresh(project, jsonOpen)
  useEffect(() => {
    if (!agentNonce) return
    const target = waiting || name
    if (!target) return
    void open(target, true).then((ok) => {
      if (!ok || !waiting) return
      setWaiting('')
      toast.success('Ask-AI built the playbook — review it below')
      if (waiting !== name) onSaved(waiting)
    })
  }, [agentNonce]) // eslint-disable-line react-hooks/exhaustive-deps

  const buildWithAgent = () => {
    const target = name || slugName(pbName || goal)
    window.dispatchEvent(new CustomEvent('twasta:agent-send-prompt', {
      detail: { project, prompt: buildBrief(target, goal, details, !!pb) },
    }))
    setWaiting(target)
    setVerify(null)
    toast.success('Sent to Ask-AI — answer its questions in the Ask-AI panel')
  }

  const rederive = async (next?: Playbook) => {
    const base = next || pb
    if (!base) return
    setBusy('derive')
    try {
      const res = await playbooksApi.repropose(project, { ...base, goal })
      adopt(res.playbook, res)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not re-derive')
    } finally {
      setBusy('')
    }
  }

  const applyJson = () => {
    if (!pb) return
    try {
      const steps = JSON.parse(json)
      if (!Array.isArray(steps)) throw new Error('Steps must be a JSON list')
      setJsonOpen(false)
      void rederive({ ...pb, steps })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid JSON')
    }
  }

  const save = async () => {
    if (!pb) return
    setBusy('save')
    try {
      const saved = await playbooksApi.save(project, { ...pb, goal })
      setPb(saved)
      toast.success('Playbook saved')
      onSaved(saved.name)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save')
    } finally {
      setBusy('')
    }
  }

  const apply = async () => {
    if (!pb) return
    setBusy('apply')
    try {
      const res = await playbooksApi.apply(project, { ...pb, goal }, Array.from(selected))
      adopt(res.playbook)
      setVerify(res.verify)
      const failed = (res.playbook.proposals?.items || []).filter((i) => i.status === 'failed').length
      if (failed) toast.error(`${failed} item(s) could not be applied — see below`)
      else toast.success('Applied')
      onSaved(res.playbook.name)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Apply failed')
    } finally {
      setBusy('')
    }
  }

  const setPersona = (id: string, persona: string) => {
    if (!pb) return
    const items = (pb.proposals?.items || []).map((i) => (i.id !== id ? i : {
      ...i, payload: { ...i.payload, target: { ...i.payload.target, persona } },
    }))
    setPb({ ...pb, proposals: { items } })
  }

  const items = pb?.proposals?.items || []
  const profile = pb?.required_profile

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 980 }}>
      <div style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={labelStyle}>Goal</div>
        <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={3}
          placeholder="e.g. Every morning, find sales orders overdue by more than 7 days, ask the customer by email whether to cancel, cancel the ones they approve after the sales manager signs off, and tell the payment gateway to refund them."
          style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical', lineHeight: 1.5 }} />
        <div style={labelStyle}>{pb ? 'What to change' : 'Details'}</div>
        <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4}
          placeholder={pb
            ? 'e.g. Ask the branch head instead of the sales manager, and skip orders under 500.'
            : 'Anything Ask-AI should know: who must approve or be asked (in the app or by email), which systems outside the app are involved, how often it runs, limits and rules it must respect.'}
          style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical', lineHeight: 1.5 }} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={pbName} onChange={(e) => setPbName(e.target.value)} placeholder="name (optional)"
            disabled={!!name} style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 320 }} />
          <span style={{ flex: 1 }} />
          <button type="button" disabled={!goal.trim() || !!busy || !!waiting} onClick={buildWithAgent}
            style={{ ...btn(true), opacity: !goal.trim() || busy || waiting ? 0.6 : 1 }}>
            {pb ? 'Revise with Ask-AI' : 'Build with Ask-AI'}
          </button>
        </div>
        {waiting && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', color: C.ink2 }}>
            <span className="material-icons" aria-hidden style={{ fontSize: 19, color: C.accent }}>smart_toy</span>
            <span style={{ flex: 1 }}>
              Ask-AI is building <code>{waiting}</code>. Answer its questions in the Ask-AI panel —
              this screen updates when it writes the playbook.
            </span>
            <button type="button" style={btn()} onClick={() => setWaiting('')}>Dismiss</button>
          </div>
        )}
      </div>

      <Questions questions={questions} problems={problems} />

      {pb && (
        <>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ ...labelStyle, flex: 1 }}>Steps ({pb.title || pb.name})</div>
              <button type="button" style={btn()} onClick={() => setJsonOpen(!jsonOpen)}>
                {jsonOpen ? 'Close editor' : 'Edit steps'}
              </button>
            </div>
            {jsonOpen ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={18} spellCheck={false}
                  style={{ ...inputStyle, height: 'auto', padding: 10, fontFamily: 'monospace', fontSize: '0.8425rem' }} />
                <div><button type="button" style={btn(true)} onClick={applyJson}>Apply edits &amp; re-derive</button></div>
              </div>
            ) : <StepTree steps={pb.steps} />}
          </div>

          {!!pb.custom_tools?.length && (
            <div>
              <div style={{ ...labelStyle, marginBottom: 8 }}>The playbook's own tools</div>
              <div style={{ ...cardStyle, overflow: 'hidden' }}>
                {pb.custom_tools.map((t, i) => (
                  <div key={t.name} style={{ padding: '10px 12px', borderTop: i ? `1px solid ${C.line}` : 'none',
                    display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span className="material-icons" aria-hidden style={{ fontSize: 19, color: C.accent, marginTop: 1 }}>build</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <code style={{ fontSize: '0.9125rem', color: C.ink }}>{t.name}</code>
                        <Chip tone={t.writes ? 'warn' : 'muted'}>{t.writes ? 'Changes data' : 'Read only'}</Chip>
                      </div>
                      {t.description && <div style={{ fontSize: '0.8425rem', color: C.ink2 }}>{t.description}</div>}
                      <div style={{ fontSize: '0.8125rem', color: C.faint }}>
                        {[...(t.uses || []).map((u) => `${(u.actions || ['view']).join(', ')} on ${u.obj_name}`),
                          ...(t.business_areas || []).map((a) => `business area ${a}`)].join(' · ') || 'no app data'}
                        {' · '}src/playbook_models/{pb.name}/tool__{t.name}.py
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile && (
            <div>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Access profile the persona needs</div>
              <div style={{ ...cardStyle, padding: 12 }}>
                <div style={{ fontSize: '0.9125rem', marginBottom: 6, color: C.ink }}>
                  Role <code>{profile.role}</code> — the least access the steps need.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', color: C.ink }}>
                  <tbody>
                    {profile.grants.map((g) => (
                      <tr key={g.obj_name} style={{ borderTop: `1px solid ${C.line}` }}>
                        <td style={{ padding: '5px 0', fontWeight: 600 }}>{g.obj_name}</td>
                        <td style={{ padding: '5px 0', color: C.ink2 }}>{g.actions.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ ...labelStyle, flex: 1 }}>Proposals</div>
              <button type="button" style={btn()} disabled={!!busy} onClick={() => void rederive()}>
                {busy === 'derive' ? 'Re-deriving…' : 'Re-derive'}
              </button>
            </div>
            <ProposalList project={project} items={items} selected={selected}
              onToggle={(id) => setSelected((cur) => {
                const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n
              })}
              onPersona={setPersona} />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={btn(true)} disabled={!!busy} onClick={() => void apply()}>
              {busy === 'apply' ? 'Applying…' : `Apply selected (${selected.size})`}
            </button>
            <button type="button" style={btn()} disabled={!!busy} onClick={() => void save()}>
              {busy === 'save' ? 'Saving…' : 'Save playbook only'}
            </button>
            <button type="button" style={btn()} onClick={onBack}>Back</button>
          </div>

          {verify && (
            <div style={{ ...cardStyle, padding: 12, borderColor: verify.ok ? C.ok : C.warn }}>
              <div style={{ ...labelStyle, marginBottom: 6, color: verify.ok ? C.ok : C.warn }}>
                {verify.ok ? 'Verified' : 'Verification — open items'}
              </div>
              {verify.checks.map((c, i) => (
                <div key={i} style={{ fontSize: '0.875rem', color: c.ok ? C.ok : C.err }}>
                  {c.ok ? '✓' : '✗'} {c.check}{c.detail ? ` — ${c.detail}` : ''}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default function PlaybookDesignerPage() {
  const { project = '' } = useParams<{ project: string }>()
  // The Ask-AI panel works on the SELECTED project; select this one so a brief
  // sent from here reaches it.
  const { currentProject, selectProject } = useProjectStore()
  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) selectProject(project)
  }, [project, currentProject, selectProject])
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [list, setList] = useState<{ name: string; title: string; goal: string; version: number; steps: number; role: string }[] | null>(null)
  const [error, setError] = useState('')
  const name = params.get('name') || ''
  const editing = params.get('pane') === 'edit'

  const reload = useCallback(async () => {
    try {
      setList((await playbooksApi.list(project)).playbooks)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load playbooks')
      setList([])
    }
  }, [project])

  useEffect(() => { void reload() }, [reload])

  const open = (n: string) => setParams(n ? { name: n, pane: 'edit' } : { pane: 'edit' })
  const back = () => { setParams({}); void reload() }
  const current = useMemo(() => (list || []).find((p) => p.name === name), [list, name])

  const remove = async (n: string) => {
    if (!window.confirm(`Delete the "${n}" playbook? Personas assigned to it stop running it.`)) return
    try {
      await playbooksApi.remove(project, n)
      toast.success('Deleted')
      void reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete it')
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 20px',
        borderBottom: `1px solid ${C.line}`, background: C.panel, fontSize: '0.875rem', color: C.ink2 }}>
        <button type="button" onClick={() => navigate(`/workbench/${encodeURIComponent(project)}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ink2, display: 'flex' }}>
          <span className="material-icons" style={{ fontSize: 19 }}>chevron_left</span>
        </button>
        <span>Advanced</span>
        <span style={{ color: C.faint }}>/</span>
        <button type="button" onClick={back} style={{ background: 'none', border: 'none', padding: 0,
          cursor: 'pointer', color: editing ? C.ink2 : C.ink, fontWeight: editing ? 400 : 600, fontSize: '0.875rem' }}>
          Agent Playbooks
        </button>
        {editing && (
          <>
            <span style={{ color: C.faint }}>/</span>
            <strong style={{ color: C.ink }}>{current?.title || name || 'New playbook'}</strong>
          </>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px', color: C.ink }}>
        {error && <div style={{ color: C.err, marginBottom: 12 }}>{error}</div>}
        {editing ? (
          <Editor key={name || 'new'} project={project} name={name}
            onSaved={(n) => { if (n !== name) setParams({ name: n, pane: 'edit' }); void reload() }}
            onBack={back} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 980 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <p style={{ margin: 0, flex: 1, fontSize: '0.9125rem', color: C.ink2, lineHeight: 1.5 }}>
                An agent playbook is a goal the application's AI works towards end to end: it reads and
                changes data through the app's own screens and rules, asks people (in the app or by a
                one-time link) and waits for approvals. At run time an administrator gives it to a
                persona, with a run cycle and an AI-credit budget.
              </p>
              <button type="button" style={btn(true)} onClick={() => open('')}>New playbook</button>
            </div>
            {list && list.length === 0 && (
              <div style={{ ...cardStyle, padding: 24, textAlign: 'center', color: C.ink2, fontSize: '0.9125rem' }}>
                No playbooks yet. Start with a goal.
              </div>
            )}
            {(list || []).map((p) => (
              <div key={p.name} style={{ ...cardStyle, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="material-icons" aria-hidden style={{ color: C.accent }}>smart_toy</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{p.title}</div>
                  <div style={{ fontSize: '0.8625rem', color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap' }}>{p.goal}</div>
                  <div style={{ fontSize: '0.8125rem', color: C.faint }}>
                    {p.steps} step(s) · profile {p.role} · v{p.version}
                  </div>
                </div>
                <button type="button" style={btn()} onClick={() => open(p.name)}>Open</button>
                <button type="button" style={btn(false, true)} onClick={() => void remove(p.name)}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
